import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  ApproveItemsPayload,
  BlockStatus,
  BulkCreateDataUpdateRequestPayload,
  BulkCreateDataUpdateRequestResponse,
  CreateDataUpdateRequestPayload,
  DataUpdateRequest,
  DataUpdateRequestKpis,
  DataUpdateRequestListFilters,
  DataUpdateRequestListResponse,
  getItemId,
  MeStatusResponse,
  RejectItemsPayload,
  RequestableField,
  SubmitItemsPayload,
  UploadUrlResponse,
  UserForRequest,
  UserForRequestListResponse,
} from '../interfaces/data-update-request.interface';

const EMPTY_STATUS: BlockStatus = {
  bloqueado: false,
  vencida: false,
  pendientes: 0,
  requestId: null,
};

/**
 * Service de "Solicitud de actualización de datos".
 *
 * Expone:
 *  - `status$` reactivo (cliente bloqueado o no) que consumen guard +
 *    interceptor + UI (navbar). El BehaviorSubject vive en este service
 *    (sin NgRx — es estado de UI puntual, no se beneficia del store).
 *  - `ensureLoaded()` / `refresh()` para sincronizar contra `/me`.
 *  - Métodos del flujo cliente: `getDetail`, `submit`, `getUploadUrl`,
 *    `confirmUpload`.
 *  - Métodos del flujo operador: listado, creación, aprobación parcial,
 *    rechazo (renovación de plazo), cancelación, KPIs.
 *
 * Las llamadas siempre van por `HttpClient`, así que pasan por
 * `ApiRestInterceptor` (auth token) y `DataUpdateBlockInterceptor`
 * (whitelist). Las whitelist patterns están coordinadas con los paths
 * que usa este service — ver §6.5 del plan.
 */
@Injectable({ providedIn: 'root' })
export class DataUpdateRequestService {
  private baseUrl = `${environment.apiNest}data-update-requests`;

  private statusSubject = new BehaviorSubject<BlockStatus>(EMPTY_STATUS);

  /** Stream reactivo del bloqueo. Componentes/guards subscriben. */
  public status$: Observable<BlockStatus> = this.statusSubject.asObservable();

  /** Snapshot sincrónico del bloqueo. Útil para el interceptor. */
  get status(): BlockStatus {
    return this.statusSubject.value;
  }

  /**
   * Flag de "ya tiré al menos una vez `/me`". Evita que el guard pegue una
   * vez por cada navegación. Se resetea en `clear()` (logout).
   */
  private loadedOnce = false;

  /**
   * Timestamp del último `refresh()` exitoso. Se usa para invalidar el
   * caché cuando pasa más de `STALE_AFTER_MS` — así si el operador crea
   * una solicitud mientras el cliente está logueado, el front se entera
   * en la próxima navegación o llamada explícita al guard.
   */
  private lastLoadedAt: number = 0;
  private static readonly STALE_AFTER_MS = 30_000;

  constructor(private http: HttpClient) {}

  // ── Estado del bloqueo ──────────────────────────────────────────────

  /**
   * Re-fetch del `/me` y actualiza el BehaviorSubject. Lo usa:
   *  - El guard tras `ensureLoaded` cuando hace falta.
   *  - Cada `submit`/`confirmUpload` para reflejar avance.
   *  - El error interceptor si el backend responde 423 (señal de stale).
   *
   * Fail-open: si el fetch falla, mantenemos el último status conocido y
   * NO bloqueamos al usuario por un error de red. Mejor un bloqueo
   * tardío que dejar al cliente sin poder operar por un timeout.
   */
  async refresh(): Promise<BlockStatus> {
    try {
      const res = await firstValueFrom(
        this.http.get<MeStatusResponse>(`${this.baseUrl}/me`),
      );
      // El shape real del backend es:
      //   { request: { id, estado, items, ... }, block: { bloqueado, vencida, pendientes } }
      // Mantenemos también compat con el shape plano `{ bloqueado, pendientes, ... }`
      // por si alguna iteración del contrato lo devolviera distinto. Por eso
      // leemos cada campo con preferencia anidado > plano.
      const pendientes =
        res?.block?.pendientes ?? res?.pendientes ?? 0;
      const vencida = !!(res?.block?.vencida ?? res?.vencida);
      const bloqueadoBackend = !!(res?.block?.bloqueado ?? res?.bloqueado);
      // `requestId` viene anidado en `request.id` en el shape real; el
      // shape plano usaría `requestId` directo.
      const requestId =
        res?.requestId ?? res?.request?.id ?? null;
      // Estado de la solicitud — `COMPLETED` y `CANCELLED` son
      // terminales y NO deben bloquear al cliente aunque el backend
      // siga devolviendo el `requestId` (caso real observado en
      // producción). Otros estados (`PENDING`, `EXPIRED`,
      // `CLOSED_MAX_REJECTS`) sí requieren acción o siguen bloqueando.
      const estadoRequest = (res?.request?.estado ?? '').toString().toUpperCase();
      const isRequestClosed =
        estadoRequest === 'COMPLETED' || estadoRequest === 'CANCELLED';

      // Bloqueamos al cliente si CUALQUIERA de estas condiciones aplica:
      //   1. El backend lo marca explícitamente (`bloqueado: true`).
      //   2. Hay items pendientes o rechazados por resolver
      //      (`pendientes > 0`) — backend cuenta ambos como "no resueltos".
      //   3. La solicitud venció sin completarse (`vencida === true`).
      //   4. Hay solicitud NO cerrada (`requestId != null` Y estado no
      //      es terminal) — cubre el caso "todos los items en SUBMITTED":
      //      pendientes=0 pero la solicitud sigue en PENDING esperando
      //      la aprobación del operador. En cuanto pasa a COMPLETED o
      //      CANCELLED (estados terminales), esta condición ya no
      //      dispara aunque `requestId` siga poblado.
      const bloqueado =
        bloqueadoBackend ||
        pendientes > 0 ||
        vencida ||
        (!!requestId && !isRequestClosed);

      // Si la solicitud está cerrada terminalmente NO queremos que el
      // `requestId` filtre al componente embed / banner del home —
      // sería un puntero muerto. Lo neutralizamos aquí también.
      const effectiveRequestId = isRequestClosed ? null : requestId;

      const status: BlockStatus = {
        bloqueado,
        vencida,
        pendientes,
        requestId: effectiveRequestId,
      };
      this.statusSubject.next(status);
      this.loadedOnce = true;
      this.lastLoadedAt = Date.now();
      return status;
    } catch {
      this.loadedOnce = true;
      // No actualizamos `lastLoadedAt` en error — la próxima llamada
      // sigue viéndolo como stale y reintenta.
      return this.statusSubject.value;
    }
  }

  /**
   * Llamado por el guard en cada navegación cliente. Refresca cuando:
   *   - no se cargó nunca,
   *   - el caché es más viejo que `STALE_AFTER_MS`,
   *   - el caller pide `{ forceRefresh: true }`.
   * Sino devuelve el cache, evitando una request por cada navegación.
   *
   * Esto resuelve el escenario "operador crea solicitud mientras el
   * cliente está logueado": en la próxima navegación o ensureLoaded,
   * el cache está stale y se va a `refresh()` que detecta el cambio.
   */
  async ensureLoaded(options: { forceRefresh?: boolean } = {}): Promise<BlockStatus> {
    if (!this.loadedOnce) {
      return this.refresh();
    }
    if (options.forceRefresh) {
      return this.refresh();
    }
    const age = Date.now() - this.lastLoadedAt;
    if (age > DataUpdateRequestService.STALE_AFTER_MS) {
      return this.refresh();
    }
    return this.status;
  }

  /**
   * Reset al logout. Sin esto, un user A queda con el status del user B
   * tras cambiar de sesión.
   */
  clear(): void {
    this.loadedOnce = false;
    this.lastLoadedAt = 0;
    this.statusSubject.next(EMPTY_STATUS);
  }

  // ── Flujo cliente ───────────────────────────────────────────────────

  /**
   * Detalle de una solicitud. **Exclusivo para operadores** — devuelve
   * `401` si lo invoca un cliente. Para el flujo cliente usar
   * `getMyDetail(id)` que apunta a `/me/:id`.
   *
   * Lo siguen usando `DataUpdateRequestDetailComponent` (admin) y
   * cualquier otro consumidor de operador que necesite el detalle por id.
   */
  getDetail(id: string): Promise<DataUpdateRequest> {
    return firstValueFrom(
      this.http.get<DataUpdateRequest>(`${this.baseUrl}/${id}`),
    );
  }

  /**
   * Detalle de una solicitud **propia** del cliente — `GET /me/:id`. El
   * backend resuelve el usuario por el JWT y valida que la solicitud
   * sea del cliente. Errores tipados:
   *   - `401` → sesión vencida / token faltante.
   *   - `403` → la solicitud existe pero no pertenece al cliente.
   *   - `404` → el id no existe.
   *
   * El `DataUpdateRequestComponent` cliente usa este endpoint; el admin
   * sigue usando `getDetail(id)`.
   */
  getMyDetail(id: string): Promise<DataUpdateRequest> {
    return firstValueFrom(
      this.http.get<DataUpdateRequest>(`${this.baseUrl}/me/${id}`),
    );
  }

  /**
   * Solicitud vigente del cliente — `GET /me`. Devuelve el `request`
   * fresco (con ids actuales) o `null`. Es la fuente de verdad para
   * resolver ids ANTES de subir/confirmar/enviar: si el operador re-emitió
   * la solicitud, los `itemId` cambian aunque el `request.id` se mantenga.
   */
  async getMyCurrent(): Promise<DataUpdateRequest | null> {
    const res = await firstValueFrom(
      this.http.get<MeStatusResponse>(`${this.baseUrl}/me`),
    );
    return res?.request ?? null;
  }

  /**
   * Resuelve `requestId` + `itemId` FRESCOS por `fieldKey` (que es
   * estable). Nunca cachear el `itemId`: se toma del `GET /me` del momento.
   * `null` si no hay solicitud o el `fieldKey` ya no está entre los items
   * (la solicitud cambió y ese dato ya no se pide).
   */
  async resolveFreshItem(
    fieldKey: string,
  ): Promise<{ requestId: string; itemId: string } | null> {
    const request = await this.getMyCurrent();
    if (!request) return null;
    const item = (request.items || []).find((i) => i.fieldKey === fieldKey);
    if (!item) return null;
    const itemId = getItemId(item);
    if (!itemId) return null;
    return { requestId: request.id, itemId };
  }

  /**
   * `true` si el error corresponde a un `itemId` obsoleto porque la
   * solicitud fue re-emitida (`code: 'ITEM_NO_VIGENTE'`). El backend lo
   * devuelve como 400; Angular deja el body en `err.error`.
   */
  isItemNoVigente(err: any): boolean {
    return (
      err?.error?.code === 'ITEM_NO_VIGENTE' || err?.code === 'ITEM_NO_VIGENTE'
    );
  }

  /**
   * Submit batch de items texto/número/fecha/booleano. Tras éxito,
   * refresca el status para detectar transiciones (e.g. último item
   * aprobado → bloqueado = false).
   */
  async submit(id: string, payload: SubmitItemsPayload): Promise<void> {
    await firstValueFrom(
      this.http.post(`${this.baseUrl}/${id}/submit`, payload),
    );
    await this.refresh();
  }

  /**
   * Paso 1 del upload: pide URL pre-firmada al backend para PUT directo
   * a S3. El front envía `contentType` para que backend firme la URL con
   * el header correcto.
   */
  getUploadUrl(
    requestId: string,
    itemId: string,
    contentType: string,
  ): Promise<UploadUrlResponse> {
    return firstValueFrom(
      this.http.post<UploadUrlResponse>(
        `${this.baseUrl}/${requestId}/items/${itemId}/file-upload-url`,
        { contentType },
      ),
    );
  }

  /**
   * Paso 3 del upload (paso 2 = PUT a S3 directo, sin pasar por aquí).
   * Confirma al backend que el archivo está en S3 y dispara OCR/risk
   * checks server-side. Tras éxito refresca el status.
   */
  async confirmUpload(
    requestId: string,
    itemId: string,
    key: string,
  ): Promise<void> {
    await firstValueFrom(
      this.http.post(
        `${this.baseUrl}/${requestId}/items/${itemId}/file-confirm`,
        { key },
      ),
    );
    await this.refresh();
  }

  // ── Flujo operador ──────────────────────────────────────────────────

  /**
   * Listado de usuarios disponibles para crear una solicitud. Todos los
   * filtros son opcionales y combinables (AND entre sí; `q` se aplica
   * encima de cualquier combinación).
   *
   * Ver `docsmd/PLAN-FRONT-SOLICITUD-ACTUALIZACION-DATOS.md` §"Instructivo
   * Backend — Filtros y ordenamiento" para el contrato completo.
   *
   * Los campos que aún no expone el backend (`sortBy`, `estadoCompliance`,
   * `createdFrom/To`, `missingDocuments`, `hasActiveRequest`) se envían
   * igual — el backend debería ignorar los que no reconozca. En el front
   * se llenan igual porque queremos que la UI esté lista para cuando el
   * backend implemente el contrato acordado.
   */
  listUsersForRequest(filters: ListUsersForRequestFilters = {}): Promise<UserForRequestListResponse> {
    const params = this.buildParams(filters);
    return firstValueFrom(
      this.http.get<UserForRequestListResponse>(`${this.baseUrl}/users`, {
        params,
      }),
    );
  }

  /**
   * Catálogo de campos solicitables. Si el backend no expone un endpoint
   * dedicado, los `RequestableField` pueden venir embebidos en
   * `listUsersForRequest` y este método queda como wrapper opcional.
   */
  getRequestableFields(): Promise<RequestableField[]> {
    return firstValueFrom(
      this.http.get<RequestableField[]>(`${this.baseUrl}/fields`),
    );
  }

  createRequest(
    payload: CreateDataUpdateRequestPayload,
  ): Promise<DataUpdateRequest> {
    return firstValueFrom(
      this.http.post<DataUpdateRequest>(`${this.baseUrl}`, payload),
    );
  }

  /**
   * Creación masiva (todo-o-nada). Una solicitud independiente por cada
   * `target`. Si UN usuario falla la validación, el backend responde
   * `400 { message, errores: [{ usuarioId, error }] }` y NO crea nada.
   *
   * El front debe atrapar el 400 y mostrar los errores por usuario en
   * la matriz; ver `BulkCreateDataUpdateRequestComponent`.
   */
  createBulkRequest(
    payload: BulkCreateDataUpdateRequestPayload,
  ): Promise<BulkCreateDataUpdateRequestResponse> {
    return firstValueFrom(
      this.http.post<BulkCreateDataUpdateRequestResponse>(
        `${this.baseUrl}/bulk`,
        payload,
      ),
    );
  }

  listRequests(
    filters: DataUpdateRequestListFilters = {},
  ): Promise<DataUpdateRequestListResponse> {
    const params = this.buildParams(filters);
    return firstValueFrom(
      this.http.get<DataUpdateRequestListResponse>(`${this.baseUrl}`, {
        params,
      }),
    );
  }

  getKpis(): Promise<DataUpdateRequestKpis> {
    return firstValueFrom(
      this.http.get<DataUpdateRequestKpis>(`${this.baseUrl}/dashboard`),
    );
  }

  approveItems(
    id: string,
    payload: ApproveItemsPayload,
  ): Promise<DataUpdateRequest> {
    return firstValueFrom(
      this.http.post<DataUpdateRequest>(
        `${this.baseUrl}/${id}/approve`,
        payload,
      ),
    );
  }

  rejectItems(
    id: string,
    payload: RejectItemsPayload,
  ): Promise<DataUpdateRequest> {
    return firstValueFrom(
      this.http.post<DataUpdateRequest>(
        `${this.baseUrl}/${id}/reject`,
        payload,
      ),
    );
  }

  cancel(id: string): Promise<void> {
    return firstValueFrom(
      this.http.patch<void>(`${this.baseUrl}/${id}/cancel`, {}),
    );
  }

  getRequestByUser(usuarioId: number): Promise<DataUpdateRequest | null> {
    return firstValueFrom(
      this.http.get<DataUpdateRequest | null>(
        `${this.baseUrl}/user/${usuarioId}`,
      ),
    );
  }

  // ── Helpers internos ────────────────────────────────────────────────

  /**
   * Serializa un objeto a params string→string aceptados por HttpClient.
   *   - `undefined` y `null` → se omiten (no se envía el key).
   *   - `string`, `number`, `boolean` → `String(v)`.
   *   - `string[]` → CSV. El instructivo del backend acordó CSV para
   *     multi-valor (`?estadoCompliance=aprobado,por_aprobar`), evita
   *     lidiar con repetición del key en Nest/query-string parsers.
   *   - Arrays vacíos → se omiten (equivale a "sin filtro").
   */
  private buildParams(obj: Record<string, any>): Record<string, string> {
    const out: Record<string, string> = {};
    for (const k of Object.keys(obj)) {
      const v = obj[k];
      if (v === undefined || v === null) continue;
      if (Array.isArray(v)) {
        if (v.length === 0) continue;
        out[k] = v.map((x) => String(x)).join(',');
        continue;
      }
      if (typeof v === 'string' && v.trim() === '') continue;
      out[k] = String(v);
    }
    return out;
  }
}

/**
 * Filtros aceptados por `GET /data-update-requests/users`. Alineado con
 * el instructivo backend — cualquier campo que el backend todavía no
 * implemente lo debe ignorar sin romper (no rechazamos requests con
 * params desconocidos).
 */
export interface ListUsersForRequestFilters {
  // ── Paginación ────────────────────────────────────────────────
  page?: number;
  limit?: number;

  // ── Búsqueda / segmentación ──────────────────────────────────
  q?: string;
  usuarioId?: number;
  tipoUsuarioId?: 0 | 1;

  // ── Ordenamiento (backend) ───────────────────────────────────
  sortBy?: 'usuarioId' | 'nombre' | 'numeroId' | 'email' | 'compliance' | 'createdAt';
  sortOrder?: 'asc' | 'desc';

  // ── Compliance ───────────────────────────────────────────────
  /** Uno o varios estados; el service los serializa como CSV. */
  estadoCompliance?:
    | Array<'aprobado' | 'por_aprobar' | 'rechazado' | 'blacklist' | 'bloqueado' | 'desconocido'>
    | 'aprobado'
    | 'por_aprobar'
    | 'rechazado'
    | 'blacklist'
    | 'bloqueado'
    | 'desconocido';

  // ── Fecha de creación del usuario ─────────────────────────────
  /** ISO 8601 (`YYYY-MM-DD` o con tiempo). Inclusivo. */
  createdFrom?: string;
  /** ISO 8601. Inclusivo — interpretar como fin del día si no viene hora. */
  createdTo?: string;

  // ── Documentos faltantes ──────────────────────────────────────
  /**
   * Documentos que le faltan al usuario (null o ''). AND entre campos
   * listados. Alias `'any'` = "al menos uno faltante" (KYC incompleto).
   */
  missingDocuments?:
    | Array<
        | 'fotoIdfrontal'
        | 'fotoIdPosterior'
        | 'selfie'
        | 'estatuto'
        | 'documentoIdTributario'
        | 'any'
      >
    | 'fotoIdfrontal'
    | 'fotoIdPosterior'
    | 'selfie'
    | 'estatuto'
    | 'documentoIdTributario'
    | 'any';

  // ── Otros filtros de conveniencia ────────────────────────────
  hasActiveRequest?: boolean;
  paisResidencia?: string;
  nacionalidad?: string;
}
